import { type IconConfig } from '@littlebird/sdk';

import { cn } from '@/lib/utils';

import {
  iconTintStyle,
  resolveEntityIcon,
  resolveEntityIconColor,
} from './icon-catalog';

interface EntityIconProps {
  icon: IconConfig | null | undefined;
  size?: number | string;
  className?: string;
  muted?: boolean;
  strokeWidth?: number;
}

export function EntityIcon({
  icon,
  size = 14,
  className,
  muted = false,
  strokeWidth,
}: EntityIconProps) {
  const resolved = resolveEntityIcon(icon);
  const color = resolveEntityIconColor(icon?.color);

  if (resolved.kind === 'emoji') {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-flex shrink-0 select-none items-center justify-center',
          className
        )}
        style={{ width: size, height: size, fontSize: size, lineHeight: 1 }}
      >
        {resolved.emoji}
      </span>
    );
  }

  const { Icon } = resolved;
  return (
    <Icon
      size={size}
      strokeWidth={strokeWidth}
      className={cn(
        'shrink-0',
        muted && color.isDefault && 'opacity-75',
        className
      )}
      style={color.isDefault ? undefined : { color: color.fg }}
    />
  );
}

interface EntityIconChipProps extends Omit<EntityIconProps, 'size'> {
  size?: number;
  subtle?: boolean;
}

const CHIP_BORDER_WIDTH = 1;

export function EntityIconChip({
  icon,
  size = 20,
  className,
  subtle = false,
  strokeWidth = 1.5,
}: EntityIconChipProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg border smoothing-default',
        className
      )}
      style={{
        ...iconTintStyle(icon?.color, subtle),
        padding: Math.round(size * 0.25) - CHIP_BORDER_WIDTH,
      }}
    >
      <EntityIcon icon={icon} size={size} strokeWidth={strokeWidth} />
    </span>
  );
}
